// One-time sessionStorage sync across browser tabs.
// Skips entirely if:
//   • Not in a browser (SSR)
//   • A persistent JWT exists in localStorage (“Remember Me”)
//   • BroadcastChannel is unsupported

/**
 * Initialize cross-tab sessionStorage sync.
 * @param {Object} options
 * @param {string} options.persistentTokenKey - localStorage key for persistent JWT.
 */
export function initSessionSync({ persistentTokenKey = 'jwtToken' } = {}) {
  // Exit on server / SSR
  if (
    typeof window === 'undefined' ||
    typeof window.localStorage === 'undefined'
  ) {
    return;
  }

  // Skip if a persistent token is present under configurable key
  if (window.localStorage.getItem(persistentTokenKey)) {
    return;
  }

  // Exit if BroadcastChannel is unavailable
  if (typeof window.BroadcastChannel !== 'function') {
    return;
  }

  const channel = 'session-sync';
  const requestSession = 'REQUEST_SESSION';
  const responseSession = 'RESPONSE_SESSION';
  const syncKey = 'SESSION_SYNCED';
  const bc = new window.BroadcastChannel(channel);

  // Listen for requests and responses
  bc.addEventListener('message', ({ data }) => {
    // On request, send current sessionStorage snapshot
    if (data === requestSession) {
      let snapshot = {};
      try {
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          snapshot[key] = sessionStorage.getItem(key);
        }
      } catch (err) {
        console.error('[sessionSync] Failed to read sessionStorage:', err);
        snapshot = {};
      }
      bc.postMessage({ type: responseSession, snapshot });
      return;
    }

    // On response, merge once and close channel
    if (data?.type === responseSession && !sessionStorage.getItem(syncKey)) {
      try {
        Object.entries(data.snapshot).forEach(([key, value]) => {
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, value);
          }
        });
      } catch (err) {
        console.error(
          '[sessionSync] Failed to merge sessionStorage:',
          err,
        );
      } finally {
        sessionStorage.setItem(syncKey, '1');
        bc.close();
      }
    }
  });

  // Kick off the handshake
  bc.postMessage(requestSession);
}
